import { FC, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem, Paper, Switch, TextField, Tooltip, Typography } from "@mui/material";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import greyDialogTitleSx from "../Dialog/greyDialogTitleSx";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import SkuSelect from "./pickers/SkuSelect";
import { useUnitName, unitOptionTokens, factorOf, baseUnitNameOf } from "./pickers/useUnitName";
import { addRecipe, deleteRecipe, fetchRecipeById, updateRecipe } from "../../store/recipeStore/recipeThunk";

const unitOptionsFor = unitOptionTokens;
let rowSeq = 0;
const nextKey = () => ++rowSeq;
const emptyRow = () => ({ _k: nextKey(), sku: null as any, skuId: "", unit: "", qty: "" });

const RecipeForm: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const { status } = useAppSelector((state) => state.recipes);
  const unitName = useUnitName();

  const [menuItemRef, setMenuItemRef] = useState("");
  const [variantRef, setVariantRef] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [components, setComponents] = useState<any[]>([emptyRow()]);

  useEffect(() => {
    if (isEditing && id) {
      dispatch(fetchRecipeById(id))
        .unwrap()
        .then((r: any) => {
          setMenuItemRef(r.menuItemRef || "");
          setVariantRef(r.variantRef || "");
          setIsActive(r.isActive !== false);
          setComponents(
            (r.components || []).map((c: any) => ({
              _k: nextKey(),
              sku: c.skuId,
              skuId: c.skuId?._id || c.skuId,
              unit: c.unit || "",
              qty: c.qty ?? "",
            }))
          );
        })
        .catch((e: any) => toast.error(e?.message || "Failed to load recipe"));
    }
  }, [id, isEditing, dispatch]);

  const updateRow = (idx: number, patch: any) => setComponents((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const onSkuChange = (idx: number, sku: any) => updateRow(idx, { sku, skuId: sku?._id || "", unit: unitOptionsFor(sku)[0] || "" });

  const onSave = async () => {
    if (!menuItemRef) return toast.error("menuItemRef is required");
    const payload = {
      menuItemRef,
      variantRef: variantRef || null,
      isActive,
      components: components
        .filter((c) => c.skuId && Number(c.qty) > 0)
        .map((c) => ({ skuId: c.skuId, qty: Number(c.qty), unit: c.unit || null })),
    };
    if (!payload.components.length) return toast.error("Add at least one component");
    try {
      if (isEditing && id) await dispatch(updateRecipe({ id, payload })).unwrap();
      else await dispatch(addRecipe(payload)).unwrap();
      navigate("/inventory/recipes");
    } catch (e: any) {
      toast.error(e?.message || "Failed to save recipe");
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await dispatch(deleteRecipe(id)).unwrap();
      navigate("/inventory/recipes");
    } catch (e: any) {
      toast.error(e?.message || "Failed to deactivate");
    }
  };

  const loading = status === ApiStatus.LOADING;

  return (
    <Box sx={{ p: 2, pr: 0, mr: "-3px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate("/inventory/recipes")}>
            <NavigateBeforeOutlinedIcon />
          </IconButton>
          <Typography variant="h6">{isEditing ? "Recipe" : "New Recipe"}</Typography>
          <Tooltip title="How to create a recipe (BOM)">
            <IconButton size="small" onClick={() => setHelpOpen(true)}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "#7a8290" }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <LoadingButton variant="contained" loading={loading} onClick={onSave}>
            {isEditing ? "Save" : "Create"}
          </LoadingButton>
          {isEditing && (
            <Button color="error" variant="outlined" onClick={onDelete}>
              Deactivate
            </Button>
          )}
        </Box>
      </Box>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ ...greyDialogTitleSx, fontSize: 18, fontWeight: 600 }}>How to create a recipe (BOM)</DialogTitle>
        <DialogContent dividers>
          <ol style={{ margin: 0, paddingLeft: 18, color: "#3c4350", lineHeight: 1.7 }}>
            <li><b>Menu Item Ref</b> = the POS MenuItem id this recipe is for; <b>Variant Ref</b> is optional (a specific size/variant).</li>
            <li>Add the <b>components</b> (raw items) and how much of each <b>one sellable</b> uses, with the unit.</li>
            <li>When that menu item is sold, the recipe explodes and the right stock is depleted (earliest expiry first).</li>
          </ol>
          <Typography sx={{ fontSize: 12, color: "#5b6472", mt: 1 }}>
            A single raw item (e.g. a 30 ml peg of whisky) is just a one-component recipe.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={5}>
          <TextField fullWidth size="small" label="Menu Item Ref (POS MenuItem _id)" value={menuItemRef} onChange={(e) => setMenuItemRef(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Variant Ref (optional)" value={variantRef} onChange={(e) => setVariantRef(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />} label="Active" />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 2.5, display: "flex", alignItems: "center", gap: 0.5 }}>
          Components (per one sellable)
          <Tooltip title="How much of each raw item one unit of this menu item consumes. The grey line shows the amount in the item's base unit.">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7a8290" }} />
          </Tooltip>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setComponents((rows) => [emptyRow(), ...rows])}
            sx={{ ml: "auto", color: "#488dff", backgroundColor: "#fff", border: "0.5px solid #488dff", borderRadius: "7px", textTransform: "none", "&:hover": { backgroundColor: "#f5f9ff", border: "0.5px solid #488dff" } }}>
            Add component
          </Button>
        </Typography>
        {components.map((row, idx) => {
          const factor = factorOf(row.sku, row.unit);
          const baseName = baseUnitNameOf(row.sku, unitName);
          const qtyNum = Number(row.qty);
          const baseQty = factor > 0 && qtyNum > 0 ? factor * qtyNum : 0;
          return (
            <Box key={row._k} sx={{ mb: 1.5, pb: 1, borderBottom: "1px dashed #e6e8ec" }}>
              <Grid container spacing={1} sx={{ alignItems: "center" }}>
                <Grid item xs={12} sm={5}>
                  <SkuSelect value={row.skuId} onChange={(sku) => onSkuChange(idx, sku)} />
                </Grid>
                <Grid item xs={5} sm={2}>
                  <TextField select fullWidth size="small" label="Unit" value={row.unit} onChange={(e) => updateRow(idx, { unit: e.target.value })}>
                    {unitOptionsFor(row.sku).map((u) => <MenuItem key={u} value={u}>{unitName(u)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={5} sm={2}>
                  <TextField fullWidth size="small" type="number" onWheel={(e) => (e.target as HTMLInputElement).blur()} label="Qty / sellable" value={row.qty} onChange={(e) => updateRow(idx, { qty: e.target.value })} />
                </Grid>
                <Grid item xs={2} sm={1}>
                  <IconButton size="small" onClick={() => setComponents((rows) => rows.filter((_, i) => i !== idx))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
              {factor > 0 && qtyNum > 0 && (
                <Typography sx={{ fontSize: 12, color: "#5b6472", mt: 0.5 }}>
                  Uses: {qtyNum} {unitName(row.unit)} × {factor} {baseName} = <b>{baseQty.toLocaleString()} {baseName}</b> per sale
                </Typography>
              )}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default RecipeForm;
